import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document } from '@contentful/rich-text-types';
import Dialog from '@shared/components/Dialog';
import { useFeatureFlags } from '@shared/hooks/useFeatureFlag';
import useLocalStorage from '@shared/hooks/useLocalStorage';
import useContent from '@shared/queries/useContent';
import { ContentfulData, ContentfulItem } from '@shared/types/contentful';
import { useState } from 'react';

import styles from './index.module.scss';

export interface AuctionsNewFeatureModalContent {
    buttonText: string;
    modalContent: Document;
    modalImage?: ContentfulItem<ContentfulData>;
    modalImageAltText?: string;
    modalTitle: string;
    page: string;
    visibilityGroup?: string;
}

interface ImageProps {
    image: ContentfulItem<ContentfulData>;
    altText: string;
}

const Image = ({ image, altText }: ImageProps) => {
    if (!(image && image.fields && image.fields.file) || !altText) {
        return <></>;
    }

    const imageFile = image.fields.file;

    return (
        <div className={styles['image-container']}>
            <img src={imageFile.url} title={altText} alt={altText} />
        </div>
    );
};

interface AuctionsNewFeatureModalProps {
    // Ensure that this list of value matches the Page dropdown in Contentful.
    remoteName: 'home' | 'my-list' | 'search' | 'vdp';
}

/**
 * Shows a Contentful Modal.
 *  Requires React-Query to be initialized
 * @param { remoteName: One of the Remotes in Contentful }
 * @returns React component
 */
export default function AuctionsNewFeatureModal({ remoteName }: AuctionsNewFeatureModalProps) {
    const { data, isLoading, isError } = useContent<AuctionsNewFeatureModalContent>({
        contentType: 'auctionsNewFeatureModal',
    });
    const [isOpen, setIsOpen] = useState(true);
    const [storedValue, setStoredValue] = useLocalStorage<string>(
        `auctionsNewFeatureModal-${remoteName}`,
        ''
    );
    const flags = useFeatureFlags();

    if (isLoading || isError || data?.length === 0) {
        return <></>;
    }

    const newestModalEntry = data
        .filter(
            content =>
                content.fields && // Ensure content has fields
                content.fields.page === remoteName && // Check if page matches remoteName
                (!content.fields?.visibilityGroup || // No visibility group, show to all
                    content.fields?.visibilityGroup === 'all' || // Visibility group is all, show to all
                    (content.fields?.visibilityGroup === 'beta' && flags?.betaGroupAccess)) // Visibility group is beta, show to beta users
        )
        .sort((a, b) => Date.parse(b.sys.updatedAt) - Date.parse(a.sys.updatedAt))[0]; // Sort and get the newest entry

    // No entry for this remote.
    if (!newestModalEntry) {
        return <></>;
    }

    // Check against localStorage to see if it's been dismissed before.
    const hash = btoa(
        `${newestModalEntry.fields.modalTitle}-${remoteName}-${newestModalEntry.sys.updatedAt}`
    );
    if (hash === storedValue) {
        return <></>;
    }

    const closeModal = () => {
        setIsOpen(false);
        setStoredValue(hash);
    };

    return (
        <Dialog isOpen={isOpen} onClose={closeModal} title={newestModalEntry.fields.modalTitle}>
            <hzn-stack>
                <hzn-heading>{newestModalEntry.fields.modalTitle}</hzn-heading>
                <hzn-stack align-x="center">
                    <Image
                        image={newestModalEntry.fields.modalImage}
                        altText={newestModalEntry.fields.modalImageAltText}
                    />
                    <hzn-text>
                        {documentToReactComponents(newestModalEntry.fields.modalContent)}
                    </hzn-text>
                </hzn-stack>
                <hzn-stack align-x="end">
                    <hzn-button onClick={closeModal}>
                        {newestModalEntry.fields.buttonText}
                    </hzn-button>
                </hzn-stack>
            </hzn-stack>
        </Dialog>
    );
}
